import React from "react";
import { useFormik } from "formik";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ControlledCustomSelect, CustomTextField } from "../../components/formsComponents";
import { ITemplateField } from "../../redux/CustomFormFields/CustomFormFields.types";
import { FileUpload } from "../../features/FileUpload/components/FileUpload";
import { uploadFile } from "../../features/FileUpload/utils";
import { fetchTemplateByCode } from "../../redux/CustomFormFields/CustomFormFieldsActions";
import { useDispatchWrapper } from "../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
} from "../../redux/messages/messagesActions";

export interface IDynamicFormRendererProps {
  fields: ITemplateField[] | null;
  templateCode: string;
  onSubmit: (fields: ITemplateField[]) => void;
  submitButtonLabel?: string;
  submitLoading?: boolean;
}

export const DynamicFormRenderer: React.FC<IDynamicFormRendererProps> = (
  props,
) => {
  const dispatch = useDispatchWrapper();
  const [resolvedFields, setResolvedFields] = React.useState<
    ITemplateField[] | null
  >(props.fields);
  const [loading, setLoading] = React.useState(!props.fields);
  const [fileMap, setFileMap] = React.useState<Record<number, File | null>>({});

  const formik = useFormik({
    initialValues: { fields: resolvedFields || [] },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      values.fields.forEach((field, index) => {
        if (field.is_required && !field.value) {
          errors[`fields[${index}].value`] = "This field is required";
        }
      });
      return Object.keys(errors).length > 0 ? { fields: errors } : {};
    },
    onSubmit: async (values) => {
      try {
        dispatch(saveLoaderProgress());
        const updatedFields = await Promise.all(
          values.fields.map(async (field, index) => {
            if (field.attachments === "Y" && fileMap[index]) {
              const asPayload = {
                page_name: "Dynamic Forms",
              };

              const url = await uploadFile(
                fileMap[index],
                "TEST",
                field.fileUrl || "",
                asPayload,
              );
              return { ...field, fileUrl: url };
            }
            return field;
          }),
        );
        props.onSubmit(updatedFields);
      } finally {
        dispatch(saveLoaderCompleted());
      }
    },
  });

  const shouldShowDescription = (field: ITemplateField, index: number): boolean => {
    if (field.field_type === "TEXT_FIELD" && field.show_description === "Y") return true;
    if (field.field_type === "DROPDOWN" && field.show_description === "Y") {
      const adminCondition = resolvedFields?.[index]?.condition;
      return formik.values.fields[index]?.value === adminCondition;
    }
    return false;
  };

  React.useEffect(() => {
    if (props.fields) {
      setResolvedFields(props.fields);
      setLoading(false);
      return;
    }
    if (!props.templateCode) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchTemplateByCode(props.templateCode).then((template) => {
      if (!cancelled) {
        setResolvedFields(template?.fields || []);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [props.fields, props.templateCode]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!resolvedFields || resolvedFields.length === 0) {
    return (
      <Box py={4}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          No dynamic form fields configured for this template.
        </Typography>
      </Box>
    );
  }

  const hasAttachments = formik.values.fields.some(
    (f) => f.attachments === "Y",
  );
  const hasDescription = formik.values.fields.some((f, i) =>
    shouldShowDescription(f, i),
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Field</TableCell>
              {hasDescription && <TableCell>Description</TableCell>}
              {hasAttachments && <TableCell>Attachment</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {formik.values.fields.map((field, index) => {
              const errorKey = `fields[${index}].value`;
              const fieldErrors =
                formik.errors.fields as Record<string, string> | undefined;
              const error = fieldErrors?.[errorKey];

              return (
                <TableRow key={index}>
                  <TableCell>
                    <Typography>{field.title}</Typography>
                  </TableCell>
                  <TableCell>
                    {field.field_type === "DROPDOWN" ? (
                      <ControlledCustomSelect
                        value={formik.values.fields[index].value || ""}
                        options={field.options}
                        placeholder="Select one"
                        displayEmpty
                        onChange={(e) =>
                          formik.setFieldValue(
                            `fields[${index}].value`,
                            e.target.value || "",
                          )
                        }
                        fullWidth
                      />
                    ) : (
                      <CustomTextField
                        value={formik.values.fields[index].value || ""}
                        onChange={(e) =>
                          formik.setFieldValue(
                            `fields[${index}].value`,
                            e.target.value,
                          )
                        }
                        fullWidth
                      />
                    )}
                    {error && (
                      <Typography color="error" variant="caption">
                        {error}
                      </Typography>
                    )}
                  </TableCell>
                  {hasDescription && (
                    <TableCell>
                      {shouldShowDescription(field, index) && (
                        <CustomTextField
                          value={formik.values.fields[index].description}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `fields[${index}].description`,
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                      )}
                    </TableCell>
                  )}
                  {hasAttachments && (
                    <TableCell>
                      {field.attachments === "Y" && (
                        <FileUpload
                          value={formik.values.fields[index].fileUrl || null}
                          onChange={(file) =>
                            setFileMap((prev) => ({ ...prev, [index]: file }))
                          }
                          onDelete={() => {
                            setFileMap((prev) => ({ ...prev, [index]: null }));
                            formik.setFieldValue(
                              `fields[${index}].fileUrl`,
                              "",
                            );
                          }}
                        />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={props.submitLoading}>
        {props.submitButtonLabel || "Submit"}
      </Button>
    </form>
  );
};
